import { useEffect, useRef, useState } from "react";
import { fileUrl } from "@/lib/api";
import { TRANS_CODE } from "@/lib/glLut";
import { layerAt } from "@/lib/layerMotion";
import { audLevel } from "@/lib/clipAudio";

export function useTransport({ videoRef, videoBRef, fxRef, viewRef, engine, clips, duration }) {
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const st = useRef({ playing: false, from: 0, ctxStart: 0, time: 0 });
  const clipsRef = useRef(clips);
  const videoClips = useRef([]);
  const fxClips = useRef([]);
  const slotRef = useRef({});
  clipsRef.current = clips.filter((c) => ["audio", "sfx", "score"].includes(c.track));
  videoClips.current = clips.filter((c) => c.track === "video");
  fxClips.current = clips.filter((c) => c.track === "fx");

  const now = () => (st.current.playing ? st.current.from + engine.ctx.currentTime - st.current.ctxStart : st.current.time);

  const load = (v, url, local) => {
    if (v.dataset.src !== url) { v.dataset.src = url; v.src = url; v.currentTime = local; return true; }
    return false;
  };
  const preroll = (v, c) => {
    if (!v) return;
    v.dataset.active = "0";
    if (!v.paused) v.pause();
    v.playbackRate = 1;
    if (!c) return;
    if (!load(v, fileUrl(c.asset_id), c.in) && !v.seeking && Math.abs(v.currentTime - c.in) > 0.05) v.currentTime = c.in;
  };
  const syncEl = (v, c, t, play, force) => {
    if (!v) return;
    if (!c) { v.dataset.active = "0"; v.playbackRate = 1; if (!v.paused) v.pause(); return; }
    const local = c.in + (t - c.start);
    if (!load(v, fileUrl(c.asset_id), local) && !v.seeking) {
      const drift = v.currentTime - local;
      if (force || Math.abs(drift) > 0.75 || (!play && Math.abs(drift) > 0.04)) { v.currentTime = local; v.playbackRate = 1; }
      else if (play) v.playbackRate = Math.abs(drift) > 0.04 ? Math.min(1.1, Math.max(0.9, 1 - drift * 0.5)) : 1;
    }
    v.dataset.active = "1";
    if (play && v.paused) v.play().catch(() => {});
    if (!play && !v.paused) v.pause();
  };
  const upcoming = (list, t, pick) => list.filter((x) => pick(x) && x.start > t && x.start - t < 4).sort((a, b) => a.start - b.start)[0];
  const syncVideo = (t, play, force = false) => {
    const els = [videoRef.current, videoBRef.current];
    const inside = (x) => t >= x.start && t < x.start + x.len;
    const act = videoClips.current.filter(inside);
    els.forEach((el, k) => {
      const c = act.find((x) => x.idx % 2 === k);
      if (c) syncEl(el, c, t, play, force);
      else preroll(el, upcoming(videoClips.current, t, (x) => x.idx % 2 === k));
    });
    const view = { a: null, b: null, mix: 0, dim: 1, t: 0, p: 0, layers: [] };
    if (act.length) {
      const n = act[act.length - 1], o = act.length > 1 ? act[0] : null;
      if (o && n.transDur > 0) {
        const p = Math.min(1, Math.max(0, (t - n.start) / n.transDur));
        view.a = els[o.idx % 2]; view.b = els[n.idx % 2];
        view.t = TRANS_CODE[n.trans.type] || 0; view.p = p;
        if (n.trans.type === "dip") { view.mix = p < 0.5 ? 0 : 1; view.dim = Math.abs(1 - 2 * p); } else if (n.trans.type === "dissolve") view.mix = p;
        if (view.a) view.a.volume = 1 - p;
        if (view.b) view.b.volume = p;
      } else {
        view.a = els[n.idx % 2];
        if (view.a) view.a.volume = 1;
      }
      act.forEach((c) => engine.setElementGain(els[c.idx % 2], audLevel(c, t - c.start)));
    }
    const fxEls = fxRef.current;
    const live = fxClips.current.filter(inside).slice(-fxEls.length);
    const slots = slotRef.current;
    const pre = fxClips.current.filter((x) => x.start > t && x.start - t < 4 && !live.includes(x))
      .sort((a, b) => a.start - b.start).slice(0, Math.max(0, fxEls.length - live.length));
    const keep = [...live, ...pre];
    Object.keys(slots).forEach((id) => !keep.find((c) => c.id === id) && delete slots[id]);
    keep.forEach((c) => {
      if (slots[c.id] !== undefined) return;
      const used = Object.values(slots);
      slots[c.id] = fxEls.findIndex((_, i) => !used.includes(i));
    });
    fxEls.forEach((el, i) => {
      const c = live.find((x) => slots[x.id] === i);
      if (c) syncEl(el, c, t, play, force);
      else preroll(el, pre.find((x) => slots[x.id] === i));
    });
    view.layers = live.map((fc) => {
      const lt = t - fc.start, f = layerAt(fc.fx, lt);
      const alpha = f.opacity * Math.min(1, f.fade_in > 0 ? lt / f.fade_in : 1, f.fade_out > 0 ? (fc.len - lt) / f.fade_out : 1);
      return { el: fxEls[slots[fc.id]], p: { ...f, alpha: Math.max(0, alpha) } };
    });
    viewRef.current = view;
  };

  const startAudio = (t) => {
    st.current.from = t;
    st.current.ctxStart = engine.ctx.currentTime;
    engine.playClips(clipsRef.current, t);
  };

  const pause = () => {
    const t = Math.min(now(), Math.max(0, duration - 0.04));
    engine.stopClips();
    st.current = { ...st.current, playing: false, time: t };
    syncVideo(t, false);
    setTime(t);
    setPlaying(false);
  };

  const play = async () => {
    await engine.resume();
    const t = st.current.time >= duration - 0.05 ? 0 : st.current.time;
    if (videoRef.current) engine.attachMedia(videoRef.current);
    if (videoBRef.current) engine.attachMedia(videoBRef.current);
    syncVideo(t, true, true);
    startAudio(t);
    st.current.playing = true;
    setPlaying(true);
  };

  const seek = (t) => {
    t = Math.max(0, Math.min(t, duration));
    st.current.time = t;
    setTime(t);
    syncVideo(t, st.current.playing, true);
    if (st.current.playing) startAudio(t);
  };

  useEffect(() => {
    if (!st.current.playing) syncVideo(st.current.time, false, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clips]);

  useEffect(() => {
    if (!playing) return;
    let raf;
    const tick = () => {
      const t = now();
      if (t >= duration) { pause(); return; }
      syncVideo(t, true);
      st.current.time = t;
      setTime(t);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, duration]);

  return { playing, time, play, pause, seek, toggle: () => (st.current.playing ? pause() : play()) };
}
